"use client";

import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { NavArrowLeft, NavArrowRight } from "iconoir-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Duplicate slides so Embla loop fills wide viewports (benefits band pattern). */
const LOOP_COPIES = 3;
/** Middle copy is the accessible set; loop clones are visual-only for AT. */
const ACCESSIBLE_LOOP_COPY = 1;

type TrackSlide = {
  card: ValueCard;
  key: string;
  logicalIndex: number;
  copy: number;
};

function buildTrackSlides(cards: ValueCard[]): TrackSlide[] {
  return Array.from({ length: LOOP_COPIES }, (_, copy) =>
    cards.map((card, index) => ({
      card,
      key: `${card.title}-${copy}`,
      logicalIndex: index,
      copy,
    }))
  ).flat();
}

/** Figma 9117:49239 — first value tile uses grey-channel elevation. */
const VALUE_ICON_SHADOW_GREY =
  "shadow-[13px_40px_12px_0px_rgba(120,120,120,0),8px_25px_11px_0px_rgba(120,120,120,0.01),5px_14px_9px_0px_rgba(120,120,120,0.05),2px_6px_7px_0px_rgba(120,120,120,0.09),1px_2px_4px_0px_rgba(120,120,120,0.1)]";

/** Figma 9117:49265+ — remaining tiles use black-channel elevation. */
const VALUE_ICON_SHADOW_DARK =
  "shadow-[20px_51px_15px_0px_rgba(0,0,0,0),13px_33px_14px_0px_rgba(0,0,0,0.01),7px_18px_12px_0px_rgba(0,0,0,0.05),3px_8px_9px_0px_rgba(0,0,0,0.09),1px_2px_5px_0px_rgba(0,0,0,0.1)]";

/** Ties the stepper buttons to the track they scroll for assistive tech. */
const CAROUSEL_TRACK_ID = "values-carousel-track";

interface ValueCard {
  title: string;
  description: string;
  iconSrc: string;
  backgroundSrc: string;
  /** Defaults to dark elevation; index 0 uses grey per Figma. */
  iconShadow?: "grey" | "dark";
}

interface ValuesCarouselSliderProps {
  cards: ValueCard[];
  previousLabel: string;
  nextLabel: string;
  className?: string;
}

export function ValuesCarouselSlider({
  cards,
  previousLabel,
  nextLabel,
  className,
}: ValuesCarouselSliderProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  // Defaults to true so the stepper is server-rendered: the tiles overflow at
  // every viewport below ~2256px, so assuming the common case avoids a layout
  // shift on hydration. The measurement below only ever hides it.
  const [hasOverflow, setHasOverflow] = useState(true);
  const trackSlides = useMemo(() => buildTrackSlides(cards), [cards]);
  const startIndex = cards.length;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      containScroll: false,
      startIndex,
    },
    [
      Autoplay({
        delay: 4500,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
      }),
    ]
  );

  // Drive the stepper off real overflow rather than a fixed breakpoint, so it
  // disappears only when nothing is hidden.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const viewport = track.parentElement;
    if (!viewport) {
      return;
    }
    const update = () => {
      const available = viewport.clientWidth;
      // Before layout is computed every measurement reads 0, which would look
      // like "everything fits" and drop the control. Only ever hide it on a
      // real measurement.
      if (available === 0) {
        return;
      }
      setHasOverflow(track.scrollWidth - available > 1);
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    observer.observe(track);
    return () => observer.disconnect();
  }, [trackSlides.length]);

  const handlePointerDown = useCallback(() => {
    emblaApi?.plugins()?.autoplay?.reset();
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
    emblaApi?.plugins()?.autoplay?.reset();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
    emblaApi?.plugins()?.autoplay?.reset();
  }, [emblaApi]);

  if (!cards.length) {
    return null;
  }

  return (
    <div
      className={cn("mt-8 overflow-hidden lg:mt-12", className)}
      data-testid="values-carousel"
    >
      <div
        ref={emblaRef}
        className="overflow-hidden"
        onPointerDown={handlePointerDown}
      >
        <ul
          ref={trackRef}
          id={CAROUSEL_TRACK_ID}
          className="flex touch-pan-y touch-pinch-zoom"
        >
          {trackSlides.map((slide) => (
            <li
              key={slide.key}
              aria-hidden={slide.copy !== ACCESSIBLE_LOOP_COPY}
              className="mr-6 flex w-[300px] shrink-0 flex-col gap-6 lg:w-[352px]"
            >
              <ValueTile card={slide.card} index={slide.logicalIndex} />
              <ValueCopy card={slide.card} />
            </li>
          ))}
        </ul>
      </div>

      {hasOverflow && (
        <div className="mt-8 flex items-center justify-center gap-3 px-6 lg:mt-10 lg:px-0">
          <Button
            variant="secondary"
            size="icon"
            // 44px keeps the stepper a comfortable touch target on phones.
            className="size-11"
            aria-label={previousLabel}
            aria-controls={CAROUSEL_TRACK_ID}
            onClick={scrollPrev}
          >
            <NavArrowLeft aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="size-11"
            aria-label={nextLabel}
            aria-controls={CAROUSEL_TRACK_ID}
            onClick={scrollNext}
          >
            <NavArrowRight aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
}

function iconShadowClass(card: ValueCard, index: number) {
  const variant = card.iconShadow ?? (index === 0 ? "grey" : "dark");
  return variant === "grey" ? VALUE_ICON_SHADOW_GREY : VALUE_ICON_SHADOW_DARK;
}

function ValueTile({ card, index }: { card: ValueCard; index: number }) {
  return (
    <div className="border-grey-200 relative h-[300px] w-full shrink-0 overflow-hidden rounded-2xl border lg:h-[358px]">
      <Image
        src={card.backgroundSrc}
        alt=""
        fill
        sizes="(max-width: 1023px) 600px, 704px"
        className="object-cover"
      />
      <div
        className={cn(
          "absolute top-1/2 left-1/2 flex size-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[20px] bg-white",
          iconShadowClass(card, index)
        )}
      >
        {/* Plain img: SVG sized by height with auto width to preserve its aspect ratio */}
        <img
          src={card.iconSrc}
          alt=""
          width={56}
          height={56}
          className="h-14 w-auto"
        />
      </div>
    </div>
  );
}

function ValueCopy({ card }: { card: ValueCard }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-ds-heading-h5 text-grey-950 font-normal">
        {card.title}
      </h3>
      <p className="text-ds-body-sm text-grey-600">{card.description}</p>
    </div>
  );
}

export type { ValueCard };
